import React, { useState, useEffect, useCallback } from 'react';
import { TagsTitle, TagsList } from './styles';
import { Popover, Tooltip, Icon, message, Popconfirm } from 'antd';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { VERSION_CONTEXT_KEYS, useVersionContext } from '../../../context/complexVersionContext';
import {
  getVersionTags,
  deleteVersionTag,
  getTagDocument,
} from '../../../utils/services/complexVersionsAPI';
import { useTranslation } from 'react-i18next';
import { TAG_SETTINGS } from './translationValues';
import Loader from '../../../components/Loader';
import CreateTagForm from './AddTagForm';

import moment from 'moment';
import { truncateText } from '../../../utils/helper';
import { CHANNELS } from '../../../utils/complexDocumentConstants';
const NAMESPACE = ['TAG_SETTINGS'];

export const TagSettings = ({ isDraftEditable }) => {
  const { t } = useTranslation(NAMESPACE);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, `TagSettings`);

  const documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(documentIdSelector, `TagSettings`);

  const isEmailSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE],
    [],
  );
  const [isEmailTemplate] = useDocumentContext(isEmailSelector, `TagSettings`);

  const versionsSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.TAGS_LIST], []);
  const [tags, updateVersionStore] = useVersionContext(versionsSelector, `TagSettings`);

  const visibleTagSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_TAG],
    [],
  );
  const [currentTag] = useVersionContext(visibleTagSelector, `TagSettings`);

  const [isFetchingTags, setIsFetchingTags] = useState(true);
  const [isDeletingTag, setIsDeletingTag] = useState(false);
  const [deleteTagPopconfirmId, setDeleteTagPopconfirmId] = useState(null);
  const [createTagFormVisible, setCreateTagFormVisible] = useState(false);

  useEffect(() => {
    const fetchTagsList = async () => {
      try {
        const response = await getVersionTags(
          isEmailTemplate ? CHANNELS.EMAIL : CHANNELS.PRINT,
          currentDesign,
          documentId,
        );
        if (response?.statusCode === 5704) {
          updateVersionStore({ [VERSION_CONTEXT_KEYS.TAGS_LIST]: response?.data });
        } else {
          message.error(
            t(TAG_SETTINGS.TAGS_FETCH_FAILED_ERROR.key, TAG_SETTINGS.TAGS_FETCH_FAILED_ERROR.value),
          );
        }
      } catch (e) {
        message.error(
          t(TAG_SETTINGS.TAGS_FETCH_FAILED_ERROR.key, TAG_SETTINGS.TAGS_FETCH_FAILED_ERROR.value),
        );
        console.error(e);
      } finally {
        setIsFetchingTags(false);
      }
    };

    if (documentId) {
      fetchTagsList();
    }
  }, [documentId]);

  const handleDeletion = async tagId => {
    try {
      setIsDeletingTag(true);
      const response = await deleteVersionTag(tagId);
      if (response?.statusCode === 5702) {
        const removedTag = tags.filter(version => version.tagId !== tagId);
        updateVersionStore({ [VERSION_CONTEXT_KEYS.TAGS_LIST]: removedTag });
      } else {
        message.error(
          t(
            TAG_SETTINGS.TAG_DELETION_FAILED_ERROR.key,
            TAG_SETTINGS.TAG_DELETION_FAILED_ERROR.value,
          ),
        );
      }
    } catch (e) {
      message.error(
        t(TAG_SETTINGS.TAG_DELETION_FAILED_ERROR.key, TAG_SETTINGS.TAG_DELETION_FAILED_ERROR.value),
      );
      console.error(e);
    } finally {
      setIsDeletingTag(false);
    }
  };

  const updateSelectedTag = tagInfo => {
    const { tagId, tagName, env } = tagInfo;
    const fetchTagDocument = async () => {
      if (tagId) {
        const response = await getTagDocument(tagId);
        if (response?.statusCode === 5709 && response?.data) {
          updateVersionStore({
            [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_TAG]: {
              tagId,
              tagName,
              env,
              tagDocument: response.data,
            },
            [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION]: undefined,
          });
        }
      }
    };
    fetchTagDocument();
  };

  const versionMap = {
    draft: t(TAG_SETTINGS.VERSION_DRAFT.key, TAG_SETTINGS.VERSION_DRAFT.value),
    sandbox: t(TAG_SETTINGS.VERSION_TESTING.key, TAG_SETTINGS.VERSION_TESTING.value),
    live: t(TAG_SETTINGS.VERSION_PUBLISHED.key, TAG_SETTINGS.VERSION_PUBLISHED.value),
  };

  return (
    <>
      <TagsTitle>
        <div className="tags-header">
          <h2>{t(TAG_SETTINGS.TAGS_TITLE.key, TAG_SETTINGS.TAGS_TITLE.value)}</h2>
          {isDraftEditable ? (
            <Popover
              visible={createTagFormVisible}
              trigger="click"
              placement="right"
              content={
                <CreateTagForm
                  createTagFormVisible={createTagFormVisible}
                  onSuccess={() => setCreateTagFormVisible(false)}
                />
              }
              onVisibleChange={visible => setCreateTagFormVisible(visible)}
            >
              <Tooltip
                title={t(
                  TAG_SETTINGS.CREATE_NEW_TAG_TOOLTIP.key,
                  TAG_SETTINGS.CREATE_NEW_TAG_TOOLTIP.value,
                )}
              >
                <button className="add-button">
                  <Icon type={'plus'} />
                </button>
              </Tooltip>
            </Popover>
          ) : null}
        </div>
      </TagsTitle>

      <TagsList>
        {isFetchingTags ? (
          <Loader />
        ) : tags?.length === 0 ? (
          <div className="tags-empty">
            {t(
              TAG_SETTINGS.NO_TAGS_AVAILABLE_MESSAGE.key,
              TAG_SETTINGS.NO_TAGS_AVAILABLE_MESSAGE.value,
            )}
          </div>
        ) : (
          tags?.map((tag, index) => (
            <div
              key={index}
              className={`tag-item ${currentTag.tagId === tag.tagId ? 'active' : ''}`}
              onClick={() => updateSelectedTag(tag)}
            >
              <div>
                <h3>
                  {truncateText(tag.tagName, 30)} - {versionMap[tag.env]}
                </h3>
                <p>
                  {t(TAG_SETTINGS.CREATED_BY_TEXT.key, TAG_SETTINGS.CREATED_BY_TEXT.value)}{' '}
                  {tag.createdBy}, {moment(tag.createdOn).format('lll')}
                </p>
              </div>
              <div>{currentTag.tagId === tag.tagId && <Icon type="check" />}</div>
              {isDraftEditable ? (
                <Popconfirm
                  onVisibleChange={visible =>
                    setDeleteTagPopconfirmId(visible ? tag.tagId : null)
                  }
                  title={t(
                    TAG_SETTINGS.DELETION_CONFIRMATION.key,
                    TAG_SETTINGS.DELETION_CONFIRMATION.value,
                  )}
                  okText={t(TAG_SETTINGS.DELETE_BUTTON.key, TAG_SETTINGS.DELETE_BUTTON.value)}
                  cancelText={t(TAG_SETTINGS.CANCEL_BUTTON.key, TAG_SETTINGS.CANCEL_BUTTON.value)}
                  onCancel={e => e.stopPropagation()}
                  onConfirm={e => {
                    e.stopPropagation();
                    handleDeletion(tag.tagId);
                  }}
                >
                  <button
                    className={`delete-button ${
                      deleteTagPopconfirmId === tag.tagId ? 'show-delete-option' : ''
                    }`}
                    onClick={e => e.stopPropagation()}
                    tabIndex={currentTag.tagId === tag.tagId ? -1 : 0}
                  >
                    <Icon type={isDeletingTag ? 'loading' : 'delete'} />
                  </button>
                </Popconfirm>
              ) : null}
            </div>
          ))
        )}
      </TagsList>
    </>
  );
};

export default TagSettings;
